'use client';

import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { X, Plus, Calendar, ArrowRight, RotateCcw, Loader2 } from 'lucide-react';
import type { SchoolViewExtra } from '@/types/api';
import { useUpdateRotationPattern, useCreateSchoolYear, useSchoolYears } from '@/hooks/use-schools';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';

interface SetupStep2Props {
  school: SchoolViewExtra;
  schoolId: string;
  onComplete: () => void;
}

export interface Step2Ref {
  save: () => Promise<boolean>;
  canSave: () => boolean;
}

export const SetupStep2Calendar = forwardRef<Step2Ref, SetupStep2Props>(
  function SetupStep2Calendar({ school, schoolId, onComplete }, ref) {
    const [localRotationPattern, setLocalRotationPattern] = useState<string[]>(
      school.rotationPattern || []
    );
    const [schoolYearStart, setSchoolYearStart] = useState('');
    const [schoolYearEnd, setSchoolYearEnd] = useState('');
    const [showAddYear, setShowAddYear] = useState(false);
    const [isAddingYear, setIsAddingYear] = useState(false);

    const updateRotationMutation = useUpdateRotationPattern(schoolId);
    const createSchoolYearMutation = useCreateSchoolYear(schoolId);
    
    const { data: schoolYears } = useSchoolYears(schoolId);

    const schoolPrimaryColor = school.primaryColor || '#1e40af';

    // Auto-populate rotation pattern when school day types change
    useEffect(() => {
      // Only auto-populate if:
      // 1. We don't have a rotation pattern yet
      // 2. School has day types defined
      // 3. Schedule type suggests it (same_every_day should auto-fill)
      if (
        localRotationPattern.length === 0 &&
        school.dayTypes &&
        school.dayTypes.length > 0 &&
        !school.rotationPattern
      ) {
        // For "same every day" schedule types, auto-fill with all day types
        if (school.scheduleType === 'same_every_day' || school.scheduleType === 'same_every_day_plus') {
          const sorted = [...school.dayTypes].sort((a, b) => a.name.localeCompare(b.name));
          setLocalRotationPattern(sorted.map(dt => dt.name));
          toast.success('Rotation pattern auto-populated. Click Save & Continue to proceed.');
        }
        // For single day type setups (common case)
        else if (school.dayTypes.length === 1) {
          setLocalRotationPattern([school.dayTypes[0].name]);
          toast.success('Rotation pattern auto-populated. Click Save & Continue to proceed.');
        }
      }
    }, [school.dayTypes, school.rotationPattern, school.scheduleType, localRotationPattern.length]);

    const getContrastColor = (hexColor: string): string => {
      const hex = hexColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? 'black' : 'white';
    };

    // Check if step has valid data (regardless of save state)
    const hasValidData = 
      localRotationPattern.length > 0 &&
      ((schoolYears && schoolYears.length > 0) || (schoolYearStart && schoolYearEnd));

    // Check if step is actually saved to backend
    const isStepSaved =
      school.rotationPattern &&
      school.rotationPattern.length > 0 &&
      schoolYears &&
      schoolYears.length > 0;

    useEffect(() => {
      if (isStepSaved) {
        onComplete();
      }
    }, [isStepSaved, onComplete]);

    // Auto-suggest school year dates
    const suggestSchoolYear = () => {
      const now = new Date();
      const year = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
      setSchoolYearStart(`${year}-08-15`);
      setSchoolYearEnd(`${year + 1}-06-01`);
    };

    const addToRotation = (dayType: string) => {
      setLocalRotationPattern([...localRotationPattern, dayType]);
    };

    const removeFromRotation = (index: number) => {
      setLocalRotationPattern(localRotationPattern.filter((_, i) => i !== index));
    };

    const clearRotation = () => {
      setLocalRotationPattern([]);
    };

    const autofillRotation = () => {
      if (school.dayTypes && school.dayTypes.length > 0) {
        let sorted = [...school.dayTypes];
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        setLocalRotationPattern(sorted.map(dt => dt.name));
      }
    };

    const handleAddSchoolYear = async () => {
      if (!schoolYearStart || !schoolYearEnd) {
        toast.error('Please enter both start and end dates');
        return;
      }
      
      setIsAddingYear(true);
      try {
        await createSchoolYearMutation.mutateAsync({
          startDate: schoolYearStart,
          endDate: schoolYearEnd,
        });
        setSchoolYearStart('');
        setSchoolYearEnd('');
        setShowAddYear(false);
        toast.success('School year created');
      } catch (error) {
        toast.error('Failed to create school year');
      } finally {
        setIsAddingYear(false);
      }
    };

    // Expose save and canSave to parent via ref
    useImperativeHandle(ref, () => ({
      canSave: () => {
        const hasRotation = localRotationPattern.length > 0;
        const hasSchoolYear = (schoolYears && schoolYears.length > 0) || (schoolYearStart && schoolYearEnd);
        return hasRotation && !!hasSchoolYear;
      },
      save: async () => {
        try {
          const promises = [];

          // Save rotation pattern
          if (localRotationPattern.length > 0) {
            const rotationMatches = JSON.stringify(localRotationPattern) === JSON.stringify(school.rotationPattern || []);
            if (!rotationMatches) {
              promises.push(updateRotationMutation.mutateAsync(localRotationPattern));
            }
          } else {
            toast.error('Please set a rotation pattern');
            return false;
          }

          // Create school year if needed
          if (schoolYearStart && schoolYearEnd && (!schoolYears || schoolYears.length === 0)) {
            promises.push(createSchoolYearMutation.mutateAsync({
              startDate: schoolYearStart,
              endDate: schoolYearEnd,
            }));
          } else if (!schoolYears || schoolYears.length === 0) {
            toast.error('Please create a school year');
            return false;
          }

          await Promise.all(promises);
          toast.success('Calendar settings saved');
          return true;
        } catch (error) {
          console.error('Step 2 save error:', error);
          toast.error('Failed to save calendar settings. Please try again.');
          return false;
        }
      }
    }));

    const hasDayTypes = school.dayTypes && school.dayTypes.length > 0;
    const hasRotation = localRotationPattern.length > 0;
    const rotationSaved = school.rotationPattern && school.rotationPattern.length > 0;
    const hasSchoolYear = schoolYears && schoolYears.length > 0;

    return (
      <div className="space-y-8">
        {/* Rotation Pattern */}
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <RotateCcw className="h-5 w-5" style={{ color: schoolPrimaryColor }} />
              Rotation Pattern
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Define the order your day types repeat. Click day types below to build the pattern.
            </p>
          </div>
          
          {!hasDayTypes ? (
            <Card className="border-2 border-dashed" style={{ borderColor: `${schoolPrimaryColor}20` }}>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">Complete Step 1 first to define day types</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Pattern Builder */}
              <div 
                className={cn(
                  "p-4 rounded-xl border-2 min-h-[70px]",
                  rotationSaved && "ring-2 ring-offset-2"
                )}
                style={{ 
                  borderColor: rotationSaved ? schoolPrimaryColor : `${schoolPrimaryColor}20`,
                  backgroundColor: rotationSaved ? `${schoolPrimaryColor}05` : 'transparent',
                  ['--tw-ring-color' as string]: rotationSaved ? `${schoolPrimaryColor}40` : undefined
                }}
              >
                {localRotationPattern.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-3">
                    Click day types below to build your rotation pattern
                  </p>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    {localRotationPattern.map((dayType, index) => (
                      <div key={index} className="flex items-center gap-1">
                        <Badge
                          className="h-10 px-4 text-base font-bold cursor-pointer hover:opacity-80 transition-opacity"
                          style={{ 
                            backgroundColor: schoolPrimaryColor,
                            color: getContrastColor(schoolPrimaryColor)
                          }}
                          onClick={() => removeFromRotation(index)}
                        >
                          <span className="font-mono text-xs mr-2 opacity-70">{index + 1}</span>
                          {dayType}
                          <X className="ml-2 h-3 w-3 opacity-70 hover:opacity-100" />
                        </Badge>
                        {index < localRotationPattern.length - 1 && (
                          <ArrowRight className="h-4 w-4 text-muted-foreground mx-1" />
                        )}
                      </div>
                    ))}
                    {localRotationPattern.length > 0 && (
                      <span className="text-sm text-muted-foreground ml-2">→ repeats</span>
                    )}
                  </div>
                )}
              </div>

              {/* Day Type Buttons */}
              <div className="flex flex-wrap gap-2">
                {school.dayTypes?.map((dayType) => (
                  <Button
                    key={dayType.id}
                    variant="outline"
                    onClick={() => addToRotation(dayType.name)}
                    className="h-10 font-semibold"
                    style={{ 
                      borderColor: `${schoolPrimaryColor}40`,
                      color: schoolPrimaryColor,
                    }}
                  >
                    + {dayType.name}
                  </Button>
                ))}
                
                <div className="flex-1" />
                
                <Button 
                  onClick={autofillRotation} 
                  variant="secondary"
                  size="sm"
                  className="h-10"
                >
                  Use All
                </Button>
                {hasRotation && (
                  <Button 
                    onClick={clearRotation} 
                    variant="ghost"
                    size="sm"
                    className="h-10"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </>
          )}
        </div>

        {/* School Year */}
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Calendar className="h-5 w-5" style={{ color: schoolPrimaryColor }} />
              School Year
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Define the current academic year dates
            </p>
          </div>
          
          {/* Existing School Years */}
          {hasSchoolYear && (
            <div className="space-y-2">
              {schoolYears.map((year) => {
                if (!year.startDate || !year.endDate) return null;
                const startDate = new Date(year.startDate);
                const endDate = new Date(year.endDate);
                
                return (
                  <Card 
                    key={year.id} 
                    className="border-2"
                    style={{ borderColor: `${schoolPrimaryColor}20`, backgroundColor: `${schoolPrimaryColor}05` }}
                  >
                    <CardContent className="py-3 px-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-lg">
                            {startDate.getFullYear()} – {endDate.getFullYear()}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} → {' '}
                            {endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                        {year.current && (
                          <Badge 
                            style={{ 
                              backgroundColor: schoolPrimaryColor, 
                              color: getContrastColor(schoolPrimaryColor) 
                            }}
                          >
                            Current
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Add New School Year */}
          {hasSchoolYear && !showAddYear ? (
            <Button
              variant="outline"
              onClick={() => setShowAddYear(true)}
              className="w-full border-dashed"
              style={{ borderColor: `${schoolPrimaryColor}40`, color: schoolPrimaryColor }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add School Year
            </Button>
          ) : (
            <Card className="border-2 border-dashed" style={{ borderColor: `${schoolPrimaryColor}20` }}>
              <CardContent className="py-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-muted-foreground">
                      {hasSchoolYear ? 'Add School Year' : 'Create School Year'}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={suggestSchoolYear}
                        className="text-xs h-7"
                        style={{ color: schoolPrimaryColor }}
                      >
                        Suggest dates
                      </Button>
                      {hasSchoolYear && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowAddYear(false);
                            setSchoolYearStart('');
                            setSchoolYearEnd('');
                          }}
                          className="text-xs h-7"
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="startDate" className="text-xs text-muted-foreground">Start Date</Label>
                      <Input
                        id="startDate"
                        type="date"
                        value={schoolYearStart}
                        onChange={(e) => setSchoolYearStart(e.target.value)}
                        className="h-10 mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="endDate" className="text-xs text-muted-foreground">End Date</Label>
                      <Input
                        id="endDate"
                        type="date"
                        value={schoolYearEnd}
                        onChange={(e) => setSchoolYearEnd(e.target.value)}
                        className="h-10 mt-1"
                      />
                    </div>
                  </div>

                  {hasSchoolYear && (
                    <Button
                      onClick={handleAddSchoolYear}
                      disabled={isAddingYear || !schoolYearStart || !schoolYearEnd}
                      className="w-full"
                      style={{ 
                        backgroundColor: schoolPrimaryColor,
                        color: getContrastColor(schoolPrimaryColor)
                      }}
                    >
                      {isAddingYear && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Add School Year
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }
);
